import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../../config';
import { motion } from 'motion/react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';

import {
  CheckCircle,
  CircleNotch,
  Crown,
  FloppyDisk,
  Lightning,
  Microphone,
  Shield
} from '@phosphor-icons/react';
const AdminPlans = () => {
  const { token } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/plans`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans || []);
      }
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPlanIcon = (planId) => {
    const icons = {
      'basic': Shield,
      'pro': Lightning,
      'partner_pro': Crown
    };
    return icons[planId] || Shield;
  };

  const getPlanColor = (planId) => {
    const colors = {
      'basic': 'from-[#718096] to-[#5C6778]',
      'pro': 'from-[#6366F1] to-[#8B5CF6]',
      'partner_pro': 'from-[#14F195] to-[#10B981]'
    };
    return colors[planId] || colors.basic;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Plans & Limits</h1>
          <p className="text-[#A0AEC0]">Manage subscription plans and their feature limits</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <CircleNotch size={48} className="text-[#6366F1] animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan, index) => {
              const Icon = getPlanIcon(plan.plan_id);
              return (
                <motion.div
                  key={plan.plan_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="glass rounded-3xl p-6"
                >
                  {/* Plan Header */}
                  <div className="mb-6">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${getPlanColor(plan.plan_id)} flex items-center justify-center mb-4`}>
                      <Icon size={32} className="text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold text-white">${plan.price_monthly}</span>
                      <span className="text-[#A0AEC0]">/month</span>
                    </div>
                    {plan.price_annual > 0 && (
                      <p className="text-[#14F195] text-sm mt-1">
                        ${plan.price_annual}/year (save ${(plan.price_monthly * 12) - plan.price_annual})
                      </p>
                    )}
                  </div>

                  {/* Limits */}
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between items-center py-2 border-b border-[rgba(255,255,255,0.1)]">
                      <span className="text-[#A0AEC0] text-sm">Max Agents</span>
                      <span className="text-white font-semibold">
                        {plan.max_agents === -1 ? 'Unlimited' : plan.max_agents}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-[rgba(255,255,255,0.1)]">
                      <span className="text-[#A0AEC0] text-sm">Voice-Enabled Agents</span>
                      <span className="text-white font-semibold">
                        {plan.max_voice_agents === -1 ? 'Unlimited' : plan.max_voice_agents}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-[rgba(255,255,255,0.1)]">
                      <span className="text-[#A0AEC0] text-sm">Voice Minutes</span>
                      <span className="text-white font-semibold">
                        {plan.voice_minutes === -1 ? 'Unlimited' : `${plan.voice_minutes}/month`}
                      </span>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="space-y-2 mb-6">
                    {plan.features && plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle size={16} className="text-[#14F195]" />
                        <span className="text-[#A0AEC0] text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Edit Button */}
                  <button
                    className="w-full px-4 py-3 rounded-xl glass hover:bg-[rgba(255,255,255,0.1)] text-white font-semibold transition-all flex items-center justify-center gap-2"
                    onClick={() => alert('Plan editing UI coming soon')}
                  >
                    <FloppyDisk size={18} />
                    Edit Plan
                  </button>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Org Overrides Section */}
        <div className="glass rounded-3xl p-6">
          <h3 className="text-xl font-bold text-white mb-4">Organization-Specific Overrides</h3>
          <p className="text-[#A0AEC0] mb-4">
            You can set custom limits for individual organizations from their detail page.
            These overrides take precedence over plan defaults.
          </p>
          <div className="p-4 rounded-xl bg-[#1A1B23] border border-[rgba(255,255,255,0.1)]">
            <p className="text-[#718096] text-sm">
              <strong>How it works:</strong> Navigate to an organization's detail page and use the Limits tab
              to set custom values. The UI will show "Custom override" next to any modified limits.
            </p>
          </div>
        </div>

        {/* Note */}
        <div className="glass rounded-2xl p-6 border-2 border-[#F59E0B]">\n          <div className="flex items-start gap-3">
            <Shield size={24} className="text-[#F59E0B] flex-shrink-0" />
            <div>
              <h4 className="text-white font-semibold mb-2">Important Note</h4>
              <p className="text-[#A0AEC0] text-sm">
                Changing plan limits here will affect the default limits for all organizations on that plan.
                For org-specific adjustments, use the organization detail page instead.
              </p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminPlans;
